<template>
  <DialogWrapper
    class="questionnaire-selector-dialog"
    appendToBody
    width="720px"
    top="5vh"
    :show.sync="dialogShow"
    :loading="questionnaireLoading"
    @save="handleConfirmQuestionnaire"
  >
    <div class="questionnaire-selector">
      <div class="selector-header">
        <div class="selector-title">
          {{ $t(`${i18nPrefix}.label.select`) }}
        </div>
        <el-input
          v-model="questionnaireKeyword"
          class="selector-search"
          suffix-icon="el-icon-search"
          :placeholder="$t(`${i18nPrefix}.placeholder.searchQuestionnaire`)"
          @input="handleQuestionnaireSearch"
        />
      </div>
      <div class="questionnaire-list">
        <div
          v-for="questionnaire in questionnaireList"
          class="questionnaire-item"
          :key="questionnaire.id"
        >
          <div class="questionnaire-row">
            <el-checkbox
              class="questionnaire-check"
              :value="tempSelectedQuestionnaireId === questionnaire.id"
              @change="handleQuestionnaireSelect(questionnaire, $event)"
            >
              <span class="questionnaire-name">{{ questionnaire.name }}</span>
            </el-checkbox>
            <span class="questionnaire-scale" :class="tempSelectedQuestionnaireId === questionnaire.id && 'is-checked'" >{{ $t(`${i18nPrefix}.label.scale`) }}{{ questionnaire.scaleScore || '-' }}</span>
            <el-button
              class="expand-btn"
              type="text"
              :icon="isQuestionnaireExpanded(questionnaire) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
              @click="handleToggleQuestionnaire(questionnaire)"
            />
          </div>
          <div v-show="isQuestionnaireExpanded(questionnaire)" class="questionnaire-detail">
            <QuestionnairePreview
              :showQuestionLabel="true"
              :questionnaire="getQuestionnaireDetail(questionnaire)"
              :items="getQuestionnaireItems(questionnaire)"
            />
          </div>
        </div>
        <div v-if="!questionnaireLoading && questionnaireList.length === 0" class="empty-questionnaire">
          {{ $t(`${i18nPrefix}.label.noQuestionnaire`) }}
        </div>
      </div>
    </div>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { getI18nPropertyValue } from '@/i18n'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { HM_QUESTIONNAIRE_STATUS } from '@/constant/questionnaire'
import QuestionnairePreview from './questionnairePreview.vue'

export default {
  components: {
    DialogWrapper,
    QuestionnairePreview
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedQuestionnaire: {
      type: Object,
      default: null
    },
    usageType: {
      type: String,
      default: null
    },
    companyId: {
      type: [Number, String],
      default: null
    },
    pageSize: {
      type: Number,
      default: 50
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      questionnaireLoading: false,
      questionnaireKeyword: null,
      questionnaireList: [],
      questionnaireDetailMap: {},
      tempSelectedQuestionnaireId: null,
      tempSelectedQuestionnaire: null,
      expandedQuestionnaireIds: [],
      debouncedQuestionnaireSearch: null
    }
  },
  computed: {
    dialogShow: {
      get () {
        return this.show
      },
      set (value) {
        this.$emit('update:show', value)
      }
    }
  },
  created () {
    this.debouncedQuestionnaireSearch = _.debounce(this.loadQuestionnaireList, 1000)
  },
  methods: {
    ...mapActions('questionnaire', ['searchHmQuestionnaires', 'getHmQuestionnaireById']),
    normalizeQuestionnaireScale (scale) {
      if (!scale) {
        return null
      }
      return `${Number(scale.max_score)}`
    },
    normalizeQuestion (question) {
      const source = question || {}
      return {
        ...source,
        id: source.id,
        type: source.question_type,
        title_zh: source.title_zh,
        title_en: source.title_en,
        options: source.options || []
      }
    },
    normalizeQuestionnaire (questionnaire) {
      const source = questionnaire || {}
      const normalized = {
        id: source.id,
        name: getI18nPropertyValue(source, 'name_zh', 'name_en'),
        scaleScore: this.normalizeQuestionnaireScale(source.score_scale),
        raw: source
      }
      const items = source.items || []
      if (!_.isEmpty(items)) {
        normalized.items = this.normalizeQuestionnaireItems(items)
      }
      return normalized
    },
    normalizeQuestionnaireItems (items) {
      return _.map(items || [], item => {
        const question = this.normalizeQuestion(item.question)
        return {
          id: item.id,
          questionId: item.question_id,
          question,
          required: item.is_required,
          weight: item.score_weight === null || item.score_weight === undefined ? null : Number(item.score_weight),
          order: item.sort_order
        }
      })
    },
    async loadQuestionnaireList () {
      this.questionnaireLoading = true
      try {
        const params = {
          include_inherited: true,
          status: HM_QUESTIONNAIRE_STATUS.ENABLED,
          page_num: 1,
          page_size: this.pageSize,
          keyword: this.questionnaireKeyword
        }
        if (this.usageType) {
          params.usage_type = this.usageType
        }
        if (this.companyId) {
          params.company_id = this.companyId
        }
        const resp = await this.searchHmQuestionnaires(params)
        if (resp) {
          const list = _.map(resp.data || [], item => this.normalizeQuestionnaire(item))
          this.questionnaireList = await Promise.all(
            _.map(list, item => this.loadQuestionnaireDetail(item, { manageLoading: false, force: true }))
          )
        }
      } finally {
        this.questionnaireLoading = false
      }
    },
    async loadQuestionnaireDetail (questionnaire, options = {}) {
      const { manageLoading = true, force = false } = options
      if (!force && this.questionnaireDetailMap[questionnaire.id]) {
        return this.questionnaireDetailMap[questionnaire.id]
      }
      if (manageLoading) {
        this.questionnaireLoading = true
      }
      try {
        const resp = await this.getHmQuestionnaireById(questionnaire.id)
        const detail = this.normalizeQuestionnaire(resp)
        const mergedDetail = {
          ...questionnaire,
          ...detail,
          items: !_.isEmpty(detail.items) ? detail.items : questionnaire.items || []
        }
        this.$set(this.questionnaireDetailMap, questionnaire.id, mergedDetail)
        return mergedDetail
      } finally {
        if (manageLoading) {
          this.questionnaireLoading = false
        }
      }
    },
    loadQuestionnaireById (id, fallback = {}) {
      const questionnaire = this.normalizeQuestionnaire({ ...fallback, id })
      return this.loadQuestionnaireDetail(questionnaire)
    },
    handleQuestionnaireSearch () {
      this.debouncedQuestionnaireSearch()
    },
    async handleOpenQuestionnaireSelector () {
      this.questionnaireKeyword = null
      this.tempSelectedQuestionnaireId = this.selectedQuestionnaire?.id || null
      this.tempSelectedQuestionnaire = this.selectedQuestionnaire
      this.expandedQuestionnaireIds = this.selectedQuestionnaire?.id ? [this.selectedQuestionnaire.id] : []
      if (this.selectedQuestionnaire?.id) {
        this.$set(this.questionnaireDetailMap, this.selectedQuestionnaire.id, this.selectedQuestionnaire)
      }
      await this.loadQuestionnaireList()
    },
    async handleQuestionnaireSelect (questionnaire, checked) {
      if (!checked) {
        this.tempSelectedQuestionnaireId = null
        this.tempSelectedQuestionnaire = null
        return
      }
      this.tempSelectedQuestionnaireId = questionnaire.id
      this.tempSelectedQuestionnaire = await this.loadQuestionnaireDetail(questionnaire)
    },
    async handleToggleQuestionnaire (questionnaire) {
      const index = _.indexOf(this.expandedQuestionnaireIds, questionnaire.id)
      if (index > -1) {
        this.expandedQuestionnaireIds.splice(index, 1)
        return
      }
      this.expandedQuestionnaireIds.push(questionnaire.id)
      await this.loadQuestionnaireDetail(questionnaire)
    },
    isQuestionnaireExpanded (questionnaire) {
      return _.includes(this.expandedQuestionnaireIds, questionnaire.id)
    },
    getQuestionnaireDetail (questionnaire) {
      return this.questionnaireDetailMap[questionnaire.id] || questionnaire
    },
    getQuestionnaireItems (questionnaire) {
      return _.get(this.getQuestionnaireDetail(questionnaire), 'items') || []
    },
    async handleConfirmQuestionnaire () {
      if (!this.tempSelectedQuestionnaire && this.tempSelectedQuestionnaireId) {
        const questionnaire = _.find(this.questionnaireList, { id: this.tempSelectedQuestionnaireId })
        if (questionnaire) {
          this.tempSelectedQuestionnaire = await this.loadQuestionnaireDetail(questionnaire)
        }
      }
      const questionnaire = this.tempSelectedQuestionnaire ? await this.loadQuestionnaireDetail(this.tempSelectedQuestionnaire) : null
      this.$emit('confirm', questionnaire)
      this.dialogShow = false
    }
  },
  watch: {
    show (value) {
      if (value) {
        this.handleOpenQuestionnaireSelector()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.questionnaire-selector-dialog {
  ::v-deep {
    .el-dialog {
      width: 720px;

      .el-dialog__body {
        padding-top: 0;
      }
    }
  }

  .questionnaire-selector {
    .selector-header {
      display: flex;
      align-items: flex-start;
      width: 630px;
      padding-bottom: 16px;
      border-bottom: 1px solid #dcdfe6;

      .selector-title {
        width: 245px;
        padding-left: 40px;
        color: #303133;
        font-size: 22px;
        line-height: 32px;
      }

      .selector-search {
        flex: 1;
      }
    }

    .questionnaire-list {
      max-height: 716px;
      padding: 0 34px 0 35px;
      overflow-y: auto;

      .questionnaire-item {
        border-bottom: 1px solid #ededed;

        .questionnaire-row {
          display: flex;
          align-items: center;
          padding: 16px 0 17px;

          .questionnaire-check {
            flex: 1;
            min-width: 0;

            ::v-deep {
              .el-checkbox__label {
                color: #606266;
                font-size: 14px;
              }

              .el-checkbox__input.is-checked + .el-checkbox__label {
                color: #0096cc;
                font-weight: 500;
              }
            }

            .questionnaire-name {
              display: inline-block;
              max-width: 340px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              vertical-align: bottom;
            }
          }

          .questionnaire-scale {
            width: 100px;
            color: #606266;
            font-size: 14px;
            text-align: right;
            white-space: nowrap;
            margin-left: 10px;

            &.is-checked {
              color: #0096cc;
            }
          }

          .expand-btn {
            width: 36px;
            margin-left: 10px;
            padding: 0;
            color: #0096cc;
            text-align: right;
          }
        }

        .questionnaire-detail {
          padding: 0 0 16px 30px;
        }
      }

      .empty-questionnaire {
        padding: 32px 0;
        color: #909399;
        font-size: 14px;
        text-align: center;
      }
    }
  }
}
</style>
